import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { ToastProvider, useToast } from '../components/Toast'
import { useDb, getRoleHomePath, getSessionUser } from '../state/DbProvider'
import { isActiveUser } from '../state/selectors'
import {
  CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY,
  CONNECTOR_SIGNUP_PENDING_NOTICE,
  CONNECTOR_SIGNUP_READY_NOTICE,
} from '../utils/connectorSignup'

type ConnectorSignupCompleteState = {
  signupNotice?: string
  signupEmail?: string
}

function JoinInner() {
  const { db, actions, loading } = useDb()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')

  const already = db ? getSessionUser(db) : null

  const inviteCode = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    return (sp.get('code') ?? '').trim().toUpperCase()
  }, [location.search])

  const inviter = useMemo(() => {
    if (!db || !inviteCode) return null
    return (
      db.users.find(
        (user) =>
          isActiveUser(user)
          && (user.role === '代理店' || user.role === '総代理店')
          && (user.agency?.inviteCode ?? '').toUpperCase() === inviteCode,
      ) ?? null
    )
  }, [db, inviteCode])

  useEffect(() => {
    if (already) navigate(getRoleHomePath(already), { replace: true })
  }, [already, navigate])

  if (already) return null

  const inviterLabel = inviter?.role === '総代理店' ? '総代理店' : '代理店'
  const canRegister = !loading && !!inviter && !!name.trim() && !!email.trim() && pw.length >= 8 && pw === pw2
  const hasInvite = inviteCode.length > 0

  return (
    <div className="mx-auto max-w-md">
      <Card title="コネクター登録" subtitle="代理店または総代理店から届いた招待URLで登録してください">
        <div className="grid gap-3 px-5 py-4">
          {!hasInvite ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              この画面は代理店または総代理店が発行した招待URL専用です。ログイン画面からの通常登録は受け付けていません。
            </div>
          ) : loading ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              招待元情報を確認しています...
            </div>
          ) : !inviter ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              招待URLが無効です。代理店または総代理店から最新の招待URLを受け取ってください。
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                招待元{inviterLabel}: <span className="font-semibold">{inviter.name}</span>
              </div>

              {inviter.role === '総代理店' ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  この登録は総代理店直属のコネクターとして作成されます。
                </div>
              ) : null}

              <label className="grid gap-1 text-sm">
                <span className="text-slate-700">招待コード</span>
                <input
                  readOnly
                  value={inviteCode}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono"
                />
              </label>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">氏名</span>
                  <input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="山田 太郎"
                  />
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">メールアドレス</span>
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="you@example.com"
                    type="email"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-700">パスワード</span>
                  <input
                    value={pw}
                    onChange={(event) => setPw(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="8文字以上"
                    type="password"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-700">確認用</span>
                  <input
                    value={pw2}
                    onChange={(event) => setPw2(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="もう一度入力"
                    type="password"
                  />
                </label>
              </div>

              <Button
                variant="primary"
                disabled={!canRegister}
                onClick={async () => {
                  if (!inviter) {
                    toast.show('有効な招待URLからアクセスしてください', 'error')
                    return
                  }
                  if (pw !== pw2) {
                    toast.show('パスワードが一致しません', 'error')
                    return
                  }

                  const res = await actions.registerConnector({
                    agencyId: inviter.id,
                    introducedById: inviter.id,
                    name,
                    email,
                    password: pw,
                  })

                  if (!res.ok) {
                    toast.show(res.message ?? '登録できませんでした', 'error')
                    return
                  }

                  const signupNotice = res.needsEmailConfirmation
                    ? CONNECTOR_SIGNUP_PENDING_NOTICE
                    : CONNECTOR_SIGNUP_READY_NOTICE

                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem(CONNECTOR_SIGNUP_EMAIL_STORAGE_KEY, email.trim())
                  }

                  navigate('/join/complete', {
                    replace: true,
                    state: {
                      signupNotice,
                      signupEmail: email.trim(),
                    } satisfies ConnectorSignupCompleteState,
                  })
                }}
              >
                登録する
              </Button>
            </>
          )}

          <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <Link to="/login" className="text-blue-600 hover:underline">
              ログインへ
            </Link>
            <Link to="/agency-signup" className="text-blue-600 hover:underline">
              代理店申請
            </Link>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            代理店申請はログイン画面から行えます。コネクター登録は代理店または総代理店の招待URLのみを受け付けます。
          </div>
        </div>
      </Card>
    </div>
  )
}

export default function JoinPage() {
  return (
    <ToastProvider>
      <JoinInner />
    </ToastProvider>
  )
}
